import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { format, startOfMonth, endOfMonth, addMonths, eachDayOfInterval, parseISO } from 'date-fns';
import React, { useState, useMemo, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  ApiError,
  BasicFacility,
  ConnectedFacilityPublic,
  IdentifyHealthcareUserResponse,
  ConnectionsService,
  HealthcareEntitiesService,
  ProviderAvailabilityPublicService,
  ScheduleTypeEnum,
  ShiftRateTypesEnum,
  ProviderAvailability,
} from '../../../../../js/api';
import { OpenAPI } from '../../../../../js/api/core/OpenAPI';
import { request as __request } from '../../../../../js/api/core/request';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { bookingRequestsQueryKeys } from '../../../../../js/app/query-keys-factories/booking-requests';
import { BaseCalendar, type BookedRange } from '../../../components/base-calendar';
import { Button } from '../../../components/button';
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogDescription,
} from '../../../components/dialog';
import { Field, Fieldset, FieldGroup, Description } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { StatusCodes } from '../../../constants/workflows';
import { reactSelectReset } from '../../../utils/utils';
import { SchedulePreview, type ShiftEntry } from './SchedulePreview';
import { ShiftConfiguration, type DateShiftConfig, type ShiftSettings } from './ShiftConfiguration';

type ModalState = 'email' | 'facility-select' | 'manual-input';

const MODAL_STATE = {
  EMAIL: 'email' as const,
  FACILITY_SELECT: 'facility-select' as const,
  MANUAL_INPUT: 'manual-input' as const,
} satisfies Record<string, ModalState>;

const DATE_FORMAT = 'yyyy-MM-dd';

type FacilityOption = {
  value: string;
  label: string;
  facility: BasicFacility | null;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const AssignmentCreateWrapper: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { user } = useContext(SessionContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useDocumentTitle('Create Assignment');

  // Core state
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [currentMonth, setCurrentMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [selectedFacility, setSelectedFacility] = useState<BasicFacility | null>(null);
  const [connectedFacility, setConnectedFacility] = useState<ConnectedFacilityPublic | null>(null);
  const [shiftConfigs, setShiftConfigs] = useState<Record<string, DateShiftConfig>>({});
  const [potentialSchedule, setPotentialSchedule] = useState<Record<
    string,
    DateShiftConfig
  > | null>(null);
  const [potentialScheduleShiftSettings, setPotentialScheduleShiftSettings] =
    useState<ShiftSettings | null>(null);

  // Submission state
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [assignmentSubmitError, setAssignmentSubmitError] = useState<string | null>(null);
  const [shiftErrors, setShiftErrors] = useState<Record<number, string[]>>({});
  const [showThankYou, setShowThankYou] = useState(false);
  const [assignmentCreated, setAssignmentCreated] = useState<boolean | null>(null);

  // Facility modal state
  const [isFacilityModalOpen, setIsFacilityModalOpen] = useState(false);
  const [tentativeFacility, setTentativeFacility] = useState<BasicFacility | null>(null);
  const changeFacilityDateKeysRef = useRef<string[] | null>(null);

  // Unauthenticated user identification state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userResponse, setUserResponse] = useState<IdentifyHealthcareUserResponse | null>(null);
  const [initialUserResponse, setInitialUserResponse] =
    useState<IdentifyHealthcareUserResponse | null>(null);
  const [isEmailChanging, setIsEmailChanging] = useState(false);
  const [assignmentRequest, setAssignmentRequest] = useState<{
    provider?: string | null;
    email?: string;
    first_name?: string;
    last_name?: string;
    title?: string | null;
    healthcare_entity_rep?: string | null;
    facility?: string | null;
    facility_name?: string | null;
    facility_city?: string | null;
    facility_state?: string | null;
  }>({
    provider: null,
    email: '',
    first_name: '',
    last_name: '',
    title: null,
    healthcare_entity_rep: null,
    facility: null,
    facility_name: null,
    facility_city: null,
    facility_state: null,
  });
  const [initialAssignmentRequest, setInitialAssignmentRequest] = useState<
    typeof assignmentRequest | null
  >(null);

  // Provider availability data
  const [allAvailableDates, setAllAvailableDates] = useState<string[]>([]);
  const [allExplicitAvailableDates, setAllExplicitAvailableDates] = useState<string[]>([]);
  const [fetchedRanges, setFetchedRanges] = useState<{ start: string; end: string }[]>([]);

  // Reset local fetch tracking and query cache when user changes
  useEffect(() => {
    setAllAvailableDates([]);
    setAllExplicitAvailableDates([]);
    setFetchedRanges([]);
    queryClient.invalidateQueries({ queryKey: ['providerAvailabilityData', token] });
  }, [user?.id, token, queryClient]);

  const dateRange = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(addMonths(currentMonth, 1));
    return {
      startOn: format(start, DATE_FORMAT),
      endOn: format(end, DATE_FORMAT),
    };
  }, [currentMonth]);

  const isAlreadyFetched = useMemo(() => {
    const start = parseISO(dateRange.startOn);
    const end = parseISO(dateRange.endOn);
    return fetchedRanges.some((range) => {
      const rangeStart = parseISO(range.start);
      const rangeEnd = parseISO(range.end);
      return start >= rangeStart && end <= rangeEnd;
    });
  }, [dateRange, fetchedRanges]);

  const {
    data: providerAvailabilityData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['providerAvailabilityData', token, dateRange.startOn, dateRange.endOn],
    queryFn: async () => {
      if (!token) throw new Error('Token is required');
      return ProviderAvailabilityPublicService.providerAvailabilityPublicRetrieve({
        availabilityToken: token,
        startOn: dateRange.startOn,
        endOn: dateRange.endOn,
      });
    },
    enabled: !!token && !isAlreadyFetched,
  });

  // Sync query data into local state
  useEffect(() => {
    if (providerAvailabilityData?.available_dates) {
      const newDates = providerAvailabilityData.available_dates as unknown as string[];
      setAllAvailableDates((prev) => Array.from(new Set([...prev, ...newDates])));
    }
    if (providerAvailabilityData?.explicit_available_dates) {
      const newExplicit = providerAvailabilityData.explicit_available_dates as unknown as string[];
      setAllExplicitAvailableDates((prev) => Array.from(new Set([...prev, ...newExplicit])));
    }
    if (providerAvailabilityData) {
      setFetchedRanges((prev) => {
        const already = prev.some(
          (r) => r.start === dateRange.startOn && r.end === dateRange.endOn
        );
        return already ? prev : [...prev, { start: dateRange.startOn, end: dateRange.endOn }];
      });
    }
  }, [providerAvailabilityData, dateRange.startOn, dateRange.endOn]);

  const [lastProviderData, setLastProviderData] = useState<ProviderAvailability | null>(null);
  useEffect(() => {
    if (providerAvailabilityData) {
      setLastProviderData(providerAvailabilityData as ProviderAvailability);
    }
  }, [providerAvailabilityData]);

  const effectiveProviderData: ProviderAvailability | null =
    (providerAvailabilityData as ProviderAvailability) || lastProviderData;

  // Merge the persisted accumulator with the latest query response during
  // render so the calendar paints fresh data on the same frame it arrives,
  // not one render later (mirrors the same gap-closing pattern in the org
  // create-assignment wrapper).
  const displayedAvailableDates = useMemo<string[]>(() => {
    const fromQuery =
      ((providerAvailabilityData as ProviderAvailability | undefined)
        ?.available_dates as unknown as string[]) ?? [];
    if (fromQuery.length === 0) return allAvailableDates;
    return Array.from(new Set([...allAvailableDates, ...fromQuery]));
  }, [allAvailableDates, providerAvailabilityData]);

  const displayedExplicitAvailableDates = useMemo<string[]>(() => {
    const fromQuery =
      ((providerAvailabilityData as ProviderAvailability | undefined)
        ?.explicit_available_dates as unknown as string[]) ?? [];
    if (fromQuery.length === 0) return allExplicitAvailableDates;
    return Array.from(new Set([...allExplicitAvailableDates, ...fromQuery]));
  }, [allExplicitAvailableDates, providerAvailabilityData]);

  // Buffer date keys: under STRICT, every available day that the provider
  // did NOT explicitly mark open (i.e., a ±3-day surround). Flagged in the
  // selected-dates list with a hazard icon so the picker sees they're
  // selecting a buffer day rather than an explicit opening.
  const bufferDateKeys = useMemo<Set<string>>(() => {
    if (effectiveProviderData?.schedule_type !== ScheduleTypeEnum.STRICT) {
      return new Set<string>();
    }
    const explicit = new Set(displayedExplicitAvailableDates);
    return new Set(displayedAvailableDates.filter((iso) => !explicit.has(iso)));
  }, [
    effectiveProviderData?.schedule_type,
    displayedAvailableDates,
    displayedExplicitAvailableDates,
  ]);

  // Blocked overlay for the public flow: there's no "own HCO" here, so we
  // never paint pending/confirmed/completed shifts — every day in the
  // visible window that isn't in the effective available set is just
  // painted as blocked (no detail, no click). The resolver on the backend
  // has already subtracted cross-HCO bookings from `available_dates`, so
  // foreign bookings naturally fall into this overlay.
  const bookedRanges = useMemo<BookedRange[]>(() => {
    const windowStart = startOfMonth(currentMonth);
    const windowEnd = endOfMonth(addMonths(currentMonth, 1));
    if (windowStart > windowEnd) return [];
    const availableSet = new Set(displayedAvailableDates);
    return eachDayOfInterval({ start: windowStart, end: windowEnd })
      .filter((day) => !availableSet.has(format(day, DATE_FORMAT)))
      .map((day) => ({ from: day, to: day, bucket: 'blocked' as const }));
  }, [displayedAvailableDates, currentMonth]);

  // Update provider token in assignmentRequest when available
  useEffect(() => {
    if (effectiveProviderData?.availability_token) {
      setAssignmentRequest((prev) => ({
        ...prev,
        provider: effectiveProviderData.availability_token,
      }));
    }
  }, [effectiveProviderData?.availability_token]);

  // Disabled dates - past dates
  const disabledDates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfThisMonth = startOfMonth(today);
    if (today.getDate() <= 1) return [];
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return eachDayOfInterval({ start: startOfThisMonth, end: yesterday });
  }, []);

  // Default shift settings for anonymous/unknown users
  const defaultConnectedFacility: ConnectedFacilityPublic = useMemo(
    () => ({
      shift_rate_types: [
        ShiftRateTypesEnum.DAY,
        ShiftRateTypesEnum.NIGHT,
        ShiftRateTypesEnum._24H,
        ShiftRateTypesEnum.CALL,
      ],
      shift_length: 12,
      shift_start_day: '07:00:00',
      shift_start_night: '19:00:00',
      shift_start_24h: '07:00:00',
    }),
    []
  );

  // Fetch connected facility details when a known facility is selected
  useEffect(() => {
    if (!selectedFacility?.id || !effectiveProviderData?.availability_token) {
      // If we have a manual facility name but no ID, use defaults
      if (assignmentRequest.facility_name?.trim()) {
        setConnectedFacility(defaultConnectedFacility);
      } else {
        setConnectedFacility(null);
      }
      return;
    }

    ConnectionsService.connectionsConnectedFacilityPublicLookupRetrieve({
      facility: selectedFacility.id,
      availabilityToken: effectiveProviderData.availability_token,
    })
      .then((data) => setConnectedFacility(data))
      .catch(() => setConnectedFacility(defaultConnectedFacility));
  }, [
    selectedFacility?.id,
    effectiveProviderData?.availability_token,
    assignmentRequest.facility_name,
    defaultConnectedFacility,
  ]);

  // --- User identification & facility selection (unauthenticated flow) ---

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleCreateRequestClick = async () => {
    const ERROR_MESSAGE = 'Error identifying healthcare user:';

    if (user) {
      // Logged-in user: auto-identify and fetch connected facilities
      const userEmail = user.email;
      setAssignmentRequest((prev) => ({
        ...prev,
        email: userEmail,
        first_name: user.first_name || prev.first_name || '',
        last_name: user.last_name || prev.last_name || '',
        healthcare_entity_rep: user.healthcare_entity_rep_id || prev.healthcare_entity_rep || null,
      }));

      setIsSubmitting(true);
      try {
        const userInfoResponse =
          await HealthcareEntitiesService.healthcareEntitiesIdentifyHealthcareUserCreate({
            requestBody: { email: userEmail },
          });

        const connectedFacilitiesResponse =
          await ConnectionsService.connectionsConnectedFacilityList({
            provider: effectiveProviderData?.id,
            status: effectiveProviderData?.id ? [StatusCodes.ACTIVE, StatusCodes.AWAIT_PRVLG] : [],
          });

        const facilities = connectedFacilitiesResponse.results.map((cf) => ({
          id: cf.facility.id,
          name: cf.facility.name,
          city: cf.facility.city,
          state: cf.facility.state,
        }));

        setUserResponse({ ...userInfoResponse, facilities });
      } catch (err) {
        console.error(ERROR_MESSAGE, err);
        setAssignmentRequest((prev) => ({ ...prev, email: userEmail }));
      } finally {
        setIsSubmitting(false);
      }
    }

    setIsFacilityModalOpen(true);
    setInitialAssignmentRequest({ ...assignmentRequest });
    setInitialUserResponse(userResponse);
    setTentativeFacility(null);

    // For non-logged-in users with email, re-fetch if needed
    const emailMismatch =
      userResponse &&
      assignmentRequest.email?.trim() &&
      userResponse.email !== assignmentRequest.email.trim();
    if (emailMismatch) {
      setUserResponse(null);
    }
    const shouldRefetch =
      !user && assignmentRequest.email?.trim() && (!userResponse || emailMismatch);
    if (shouldRefetch && assignmentRequest.email) {
      setIsSubmitting(true);
      try {
        const response =
          await HealthcareEntitiesService.healthcareEntitiesIdentifyHealthcareUserCreate({
            requestBody: { email: assignmentRequest.email.trim() },
          });
        setUserResponse(response);
        setAssignmentRequest((prev) => ({
          ...prev,
          first_name: response.first_name || prev.first_name || '',
          last_name: response.last_name || prev.last_name || '',
          healthcare_entity_rep: response.healthcare_entity_rep_id || null,
        }));
      } catch (err) {
        console.error(ERROR_MESSAGE, err);
        setAssignmentRequest((prev) => ({ ...prev, email: '' }));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleModalClose = () => {
    setIsFacilityModalOpen(false);
    setTentativeFacility(null);
  };

  const handleModalCancel = () => {
    const emailChanged = initialAssignmentRequest?.email !== assignmentRequest.email;
    if (initialAssignmentRequest) {
      setAssignmentRequest(initialAssignmentRequest);
      setInitialAssignmentRequest(null);
    }
    if (initialUserResponse !== null && !emailChanged) {
      setUserResponse(initialUserResponse);
    } else if (emailChanged) {
      setUserResponse(null);
    }
    setInitialUserResponse(null);
    setIsEmailChanging(false);
    handleModalClose();
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (assignmentRequest.email?.trim() && userResponse && !isEmailChanging) {
      return;
    }
    if (!assignmentRequest.email?.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response =
        await HealthcareEntitiesService.healthcareEntitiesIdentifyHealthcareUserCreate({
          requestBody: { email: assignmentRequest.email.trim() },
        });
      setUserResponse(response);
      setIsEmailChanging(false);
      setAssignmentRequest((prev) => ({
        ...prev,
        first_name: response.first_name || prev.first_name || '',
        last_name: response.last_name || prev.last_name || '',
        healthcare_entity_rep: response.healthcare_entity_rep_id || null,
      }));
    } catch (err) {
      console.error('Error identifying healthcare user:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Facility options from userResponse (unauthenticated) or connected facilities (authenticated)
  const facilityOptions: FacilityOption[] = useMemo(() => {
    if (!userResponse?.facilities) return [];
    const facilityOpts = userResponse.facilities.map((facility) => ({
      value: facility.id || '',
      label: facility.name || 'Unnamed Facility',
      facility: facility,
    }));
    const hasAccount = !!userResponse?.healthcare_entity_rep_id;
    if (hasAccount) {
      return facilityOpts;
    }
    return [...facilityOpts, { value: 'OTHER', label: 'Other', facility: null }];
  }, [userResponse?.facilities, userResponse?.healthcare_entity_rep_id]);

  const tentativeFacilityOption = useMemo(() => {
    if (!tentativeFacility) return null;
    if ((tentativeFacility.id as any) === 'OTHER') {
      return facilityOptions.find((option) => option.value === 'OTHER') || null;
    }
    return facilityOptions.find((option) => option.facility?.id === tentativeFacility.id) || null;
  }, [tentativeFacility, facilityOptions]);

  const isOtherFacilitySelected = tentativeFacilityOption?.value === 'OTHER';

  // Determine which modal state we're in
  const modalState: ModalState = useMemo(() => {
    if (isEmailChanging || !assignmentRequest.email?.trim() || !userResponse) {
      return MODAL_STATE.EMAIL;
    }
    if (userResponse.facilities && userResponse.facilities.length > 0) {
      return MODAL_STATE.FACILITY_SELECT;
    }
    return MODAL_STATE.MANUAL_INPUT;
  }, [assignmentRequest.email, userResponse, isEmailChanging]);

  // Whether a facility has been chosen (either selected or manual)
  const hasFacility = !!selectedFacility || !!assignmentRequest.facility_name?.trim();

  const makeFacilityRef = (f: BasicFacility) => ({
    id: f.id || '',
    name: f.name || '',
  });

  // Handle facility confirmation from modal (for per-shift facility override)
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleFacilityConfirm = useCallback(() => {
    if (!tentativeFacility) return;

    // Handle "Other" (manual) facility — store in assignmentRequest, not selectedFacility
    if ((tentativeFacility.id as any) === 'OTHER') {
      const trimmedName = tentativeFacility.name?.trim();
      const trimmedCity = tentativeFacility.city?.trim();
      setAssignmentRequest((prev) => ({
        ...prev,
        facility: undefined,
        facility_name: trimmedName || null,
        facility_city: trimmedCity || null,
        facility_state: tentativeFacility.state
          ? (tentativeFacility.state as unknown as string)
          : null,
      }));
      setSelectedFacility(null);
      handleModalClose();
      setInitialAssignmentRequest(null);
      setInitialUserResponse(null);
      return;
    }

    // Known facility
    const facilityRef = makeFacilityRef(tentativeFacility);
    const dateKeys = changeFacilityDateKeysRef.current;

    if (dateKeys && dateKeys.length > 0) {
      // Per-shift override: apply only to selected date rows
      setShiftConfigs((prev) => {
        const updated = { ...prev };
        for (const key of dateKeys) {
          const existing = updated[key] || { shiftType: null, onCall: false, facility: null };
          updated[key] = { ...existing, facility: facilityRef };
        }
        return updated;
      });
    } else {
      // Assignment-level facility change
      setSelectedFacility(tentativeFacility);
      setAssignmentRequest((prev) => ({
        ...prev,
        facility: tentativeFacility.id || undefined,
        facility_name: tentativeFacility.name?.trim() || null,
        facility_city: tentativeFacility.city?.trim() || null,
        facility_state: tentativeFacility.state
          ? (tentativeFacility.state as unknown as string)
          : null,
      }));
      setShiftConfigs((prev) => {
        const updated = { ...prev };
        for (const [key, config] of Object.entries(updated)) {
          updated[key] = { ...config, facility: facilityRef };
        }
        for (const date of selectedDates) {
          const key = format(date, DATE_FORMAT);
          if (!updated[key]) {
            updated[key] = { shiftType: null, onCall: false, facility: facilityRef };
          }
        }
        return updated;
      });
    }

    changeFacilityDateKeysRef.current = null;
    handleModalClose();
    setInitialAssignmentRequest(null);
    setInitialUserResponse(null);
  }, [tentativeFacility, selectedDates]);

  // When new dates are selected, ensure they have a facility config
  useEffect(() => {
    if (!selectedFacility) return;
    const facilityRef = makeFacilityRef(selectedFacility);
    setShiftConfigs((prev) => {
      const updated = { ...prev };
      let changed = false;
      for (const date of selectedDates) {
        const key = format(date, DATE_FORMAT);
        if (!updated[key]) {
          updated[key] = { shiftType: null, onCall: false, facility: facilityRef };
          changed = true;
        } else if (!updated[key].facility) {
          updated[key] = { ...updated[key], facility: facilityRef };
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, [selectedDates, selectedFacility]);

  // --- Submission ---

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleAssignmentSubmit = async (shiftEntries: ShiftEntry[], comment: string) => {
    if (shiftEntries.length === 0) {
      setAssignmentSubmitError('Please add at least one shift');
      return;
    }
    if (!assignmentRequest.email?.trim()) {
      setAssignmentSubmitError('Email is required');
      return;
    }
    if (!selectedFacility && !assignmentRequest.facility_name?.trim()) {
      setAssignmentSubmitError('Facility is required');
      return;
    }
    if (!assignmentRequest.first_name?.trim() || !assignmentRequest.last_name?.trim()) {
      setAssignmentSubmitError('First name and last name are required');
      return;
    }

    setIsSubmittingAssignment(true);
    setAssignmentSubmitError(null);
    setShiftErrors({});

    const TIME_FORMAT = 'HH:mm:ss';
    try {
      const shifts = shiftEntries.map((entry) => ({
        facility: entry.facility?.id || selectedFacility?.id || undefined,
        shift_type: entry.shiftType,
        shift_rate_type: (entry.onCall
          ? ShiftRateTypesEnum.CALL
          : entry.shiftType) as ShiftRateTypesEnum,
        start_on: format(entry.startTime, DATE_FORMAT),
        start_time: format(entry.startTime, TIME_FORMAT),
        end_on: format(entry.endTime, DATE_FORMAT),
        end_time: format(entry.endTime, TIME_FORMAT),
        bonus_amount: entry.bonus_amount || 0,
        holiday_amount: entry.holiday_amount || 0,
      }));

      const response = (await __request(OpenAPI, {
        method: 'POST',
        url: '/api/assignments/assignment-request/',
        body: {
          provider: effectiveProviderData?.availability_token || null,
          email: assignmentRequest.email,
          first_name: assignmentRequest.first_name,
          last_name: assignmentRequest.last_name,
          title: assignmentRequest.title || null,
          healthcare_entity_rep: assignmentRequest.healthcare_entity_rep || null,
          facility: selectedFacility?.id || null,
          facility_name: assignmentRequest.facility_name || null,
          facility_city: assignmentRequest.facility_city || null,
          facility_state: assignmentRequest.facility_state || null,
          shifts,
          comment: comment.trim() || '',
        },
        mediaType: 'application/json',
      })) as any;

      const responseStatus = response?.$responseStatus;
      if (responseStatus === 201) {
        setAssignmentCreated(true);
      } else if (responseStatus === 202) {
        setAssignmentCreated(false);
      }

      if (user) {
        queryClient.invalidateQueries({
          queryKey: bookingRequestsQueryKeys.allLists(),
        });
        toast.success('Assignment request submitted successfully!');
        navigate('/app/organization/assignments');
      } else {
        setShowThankYou(true);
      }
    } catch (err) {
      if (err instanceof ApiError && err.body?.shifts && Array.isArray(err.body.shifts)) {
        const parsedShiftErrors: Record<number, string[]> = {};
        err.body.shifts.forEach((shiftErrorObj: Record<string, string[]>, index: number) => {
          if (
            shiftErrorObj &&
            typeof shiftErrorObj === 'object' &&
            Object.keys(shiftErrorObj).length > 0
          ) {
            const errorMessages: string[] = [];
            Object.values(shiftErrorObj).forEach((dateErrors) => {
              if (Array.isArray(dateErrors)) errorMessages.push(...dateErrors);
            });
            if (errorMessages.length > 0) parsedShiftErrors[index] = errorMessages;
          }
        });
        setShiftErrors(parsedShiftErrors);
      } else {
        const fallback = 'Failed to submit assignment request. Please try again.';
        let errorMessage = fallback;
        if (err instanceof ApiError) {
          const body = err.body;
          if (Array.isArray(body)) {
            errorMessage = body.join('. ');
          } else if (body && typeof body === 'object') {
            if (body.detail) {
              errorMessage = body.detail;
            } else if (body.message) {
              errorMessage = body.message;
            } else if (body.non_field_errors) {
              errorMessage = Array.isArray(body.non_field_errors)
                ? body.non_field_errors.join('. ')
                : body.non_field_errors;
            }
          } else if (typeof body === 'string') {
            errorMessage = 'An error occurred. Please contact support.';
          }
          if (errorMessage === fallback && err.message && err.message !== 'Internal Server Error') {
            errorMessage = err.message;
          }
        } else if (err instanceof Error && err.message) {
          errorMessage = err.message;
        }
        setAssignmentSubmitError(errorMessage);
      }
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  // --- Render guards ---

  if (!token) {
    return (
      <div className="tw:p-4 tw:max-w-6xl tw:mx-auto tw:w-full">
        <div className="tw:bg-white tw:rounded-xl tw:p-8 tw:border tw:border-zinc-200 tw:text-center">
          <p className="tw:text-stoplight-danger">Invalid token</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw:p-4 tw:max-w-6xl tw:mx-auto tw:w-full">
        <div className="tw:bg-white tw:rounded-xl tw:p-8 tw:border tw:border-zinc-200 tw:text-center">
          <p className="tw:text-stoplight-danger">Error loading provider availability</p>
        </div>
      </div>
    );
  }

  if (!effectiveProviderData && !isLoading) {
    return (
      <div className="tw:p-4 tw:max-w-6xl tw:mx-auto tw:w-full">
        <div className="tw:bg-white tw:rounded-xl tw:p-8 tw:border tw:border-zinc-200 tw:text-center">
          <p className="tw:text-zinc-500">No data available</p>
        </div>
      </div>
    );
  }

  // Thank you page for non-logged-in users
  if (showThankYou) {
    return (
      <div className="tw:p-4 tw:max-w-4xl tw:mx-auto tw:w-full">
        <div className="tw:bg-white tw:rounded-xl tw:p-8 tw:border tw:border-zinc-200 tw:text-center">
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-4">
            <div className="tw:h-20 tw:w-20 tw:flex tw:items-center tw:justify-center tw:text-brand-green-0 tw:!text-8xl tw:mb-4">
              <i className="fa-solid fa-badge-check" />
            </div>
            <div className="tw:max-w-md">
              <h2 className="tw:text-2xl tw:font-semibold tw:text-zinc-900 tw:mb-3">Thank You!</h2>
              <p className="tw:text-base tw:text-zinc-500">
                {assignmentCreated
                  ? 'Your request has been created'
                  : 'Your request has been received'}
              </p>
              <p className="tw:text-base tw:text-zinc-700">
                {assignmentCreated
                  ? `${effectiveProviderData?.display_name} is being notified of your request and you will be notified when they respond.`
                  : `We will review your assignment request for ${effectiveProviderData?.display_name} and get back to you as soon as possible.`}
              </p>
              <div className="tw:flex tw:flex-col tw:items-start tw:justify-start tw:gap-2 tw:max-w-fit tw:mx-auto tw:mt-6">
                <div className="tw:flex tw:items-start tw:justify-start tw:gap-4">
                  <i className="fa-solid fa-envelope tw:text-zinc-400 tw:mt-1" />
                  <a
                    href={`mailto:${window.SENDIT_CONTACT_INFO.healthcare_entity_support_email}`}
                    className="tw:text-brand-green-1 tw:!no-underline tw:text-left tw:break-all"
                  >
                    {window.SENDIT_CONTACT_INFO.healthcare_entity_support_email}
                  </a>
                </div>
                <div className="tw:flex tw:items-start tw:justify-start tw:gap-4">
                  <i className="fa-solid fa-phone tw:text-zinc-400 tw:mt-1" />
                  <a
                    href={`tel:${window.SENDIT_CONTACT_INFO.healthcare_entity_phone_text}`}
                    className="tw:text-brand-green-1 tw:!no-underline tw:text-left"
                  >
                    {window.SENDIT_CONTACT_INFO.healthcare_entity_phone_text}
                  </a>
                </div>
              </div>
              <div className="tw:mt-8">
                <Button color="brandgreen0" onClick={() => window.location.reload()}>
                  Request More Dates
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw:p-4 tw:max-w-6xl tw:mx-auto tw:w-full">
      <div className="tw:flex tw:flex-col-reverse tw:lg:flex-row tw:gap-4 tw:lg:gap-2">
        {/* Calendar and Scheduler */}
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div
            className={clsx(
              'tw:md:bg-white tw:rounded-xl tw:md:p-4 tw:md:border tw:border-zinc-200 tw:relative',
              potentialSchedule && potentialScheduleShiftSettings && 'tw:hidden'
            )}
          >
            {isLoading && (
              <div className="tw:absolute tw:inset-0 tw:bg-white/50 tw:z-10 tw:flex tw:items-center tw:justify-center tw:rounded-xl">
                <div className="tw:animate-spin tw:rounded-full tw:h-8 tw:w-8 tw:border-2 tw:border-zinc-300 tw:border-t-brand-green-0" />
              </div>
            )}
            <div className="tw:flex tw:flex-col">
              <div className="tw:flex tw:flex-col tw:items-start tw:justify-center">
                <h2 className="tw:!text-base tw:!font-semibold tw:!text-brand-green-0 tw:!mb-1">
                  Select Available Days
                </h2>
                <p className="tw:hidden tw:md:block tw:!text-xs tw:font-semibold tw:!text-zinc-500">
                  Hold Shift + click to select ranges of days
                </p>
              </div>
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-2 tw:mt-1 tw:mb-4">
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="tw:w-4 tw:h-4 tw:rounded tw:bg-brand-green-2 tw:border-solid tw:border tw:border-brand-green-1/50" />
                  <span className="tw:text-xs tw:text-zinc-600">Available</span>
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="tw:w-4 tw:h-4 tw:rounded tw:bg-brand-green-0" />
                  <span className="tw:text-xs tw:text-zinc-600">Selected</span>
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="calendar-blocked-pattern tw:w-4 tw:h-4 tw:rounded tw:border tw:border-zinc-300" />
                  <span className="tw:text-xs tw:text-zinc-600">Blocked</span>
                </div>
              </div>
            </div>
            <BaseCalendar
              availableDates={displayedAvailableDates}
              // STRICT is the only mode that needs the explicit/auto split:
              // explicit allows render solid green, auto ±3 days render as
              // plain default cells (per the `.rdp-day_available_auto` CSS
              // override). OPEN already returns empty explicit_available_dates,
              // and CLOSED treats every available day as explicit — so for
              // both, omitting the prop is the right call (no split at all).
              explicitAvailableDates={
                effectiveProviderData?.schedule_type === ScheduleTypeEnum.STRICT
                  ? displayedExplicitAvailableDates
                  : undefined
              }
              selectedDates={selectedDates}
              onSelectedDatesChange={setSelectedDates}
              numberOfMonths={2}
              disabled={!!potentialSchedule}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              disabledDates={disabledDates}
              bookedRanges={bookedRanges}
              className="availability-calendar justify-self-center"
            />
            {!hasFacility && (
              <div className="tw:flex tw:justify-end tw:my-4">
                <Button color="brand-green-outline" onClick={handleCreateRequestClick}>
                  Create Request
                </Button>
              </div>
            )}
          </div>

          {/* Shift Configuration or Schedule Preview */}
          {hasFacility &&
            (potentialSchedule && potentialScheduleShiftSettings ? (
              <SchedulePreview
                selectedDates={selectedDates}
                shiftConfigs={potentialSchedule}
                shiftSettings={potentialScheduleShiftSettings}
                onBack={() => {
                  setPotentialSchedule(null);
                  setPotentialScheduleShiftSettings(null);
                  setShiftErrors({});
                }}
                onRemoveDate={(dateKey) => {
                  setSelectedDates((prev) =>
                    prev.filter((date) => format(date, DATE_FORMAT) !== dateKey)
                  );
                  setPotentialSchedule((prev) => {
                    if (!prev) return null;
                    const updated = { ...prev };
                    delete updated[dateKey];
                    return Object.keys(updated).length > 0 ? updated : null;
                  });
                }}
                onSubmit={handleAssignmentSubmit}
                isSubmitting={isSubmittingAssignment}
                submitError={assignmentSubmitError}
                shiftErrors={shiftErrors}
                bufferDateKeys={bufferDateKeys}
              />
            ) : (
              <ShiftConfiguration
                connectedFacility={connectedFacility}
                selectedDates={selectedDates}
                shiftConfigs={shiftConfigs}
                onShiftConfigChange={setShiftConfigs}
                onPreviewSchedule={(config, shiftSettings) => {
                  setPotentialSchedule(config);
                  setPotentialScheduleShiftSettings(shiftSettings);
                }}
                bufferDateKeys={bufferDateKeys}
                onRemoveAllDates={() => {
                  setSelectedDates([]);
                  setShiftConfigs({});
                }}
                onChangeFacility={(dateKeys) => {
                  changeFacilityDateKeysRef.current = dateKeys;
                  setTentativeFacility(null);
                  setIsFacilityModalOpen(true);
                }}
              />
            ))}
        </div>

        {/* Provider and Facility info sidebar */}
        <aside className="tw:md:bg-white tw:md:rounded-xl tw:md:py-4 tw:lg:p-6 tw:border-b tw:pb-3 tw:md:border tw:border-zinc-200 tw:flex tw:flex-row tw:lg:flex-col tw:gap-1 tw:md:gap-4 tw:lg:min-w-64 tw:h-fit">
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:md:gap-3 tw:flex-1">
            <div className="tw:h-12 tw:w-12 tw:md:h-16 tw:md:w-16 tw:rounded-full tw:bg-brand-green-0/10 tw:flex tw:items-center tw:justify-center tw:text-brand-green-0 tw:text-lg tw:md:text-2xl">
              <i className="fas fa-xl fa-user-doctor" />
            </div>
            <div className="tw:text-center tw:min-h-[3rem] tw:flex tw:flex-col tw:justify-center">
              {effectiveProviderData ? (
                <>
                  <p className="tw:text-xs tw:md:text-sm tw:font-semibold tw:text-zinc-900 tw:!mb-1">
                    {effectiveProviderData.display_name}
                  </p>
                  {effectiveProviderData.specialties &&
                    effectiveProviderData.specialties.length > 0 && (
                      <p className="tw:text-[8pt] tw:md:text-xs tw:text-zinc-500 tw:!mb-0">
                        {effectiveProviderData.specialties[0]}
                      </p>
                    )}
                </>
              ) : (
                <div className="tw:h-4 tw:w-24 tw:bg-zinc-200 tw:animate-pulse tw:rounded tw:mx-auto" />
              )}
            </div>
            {effectiveProviderData?.phone_number && (
              <div className="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:text-xs tw:md:text-sm">
                <i className="tw:!hidden tw:md:!block fa-solid fa-phone tw:text-zinc-400" />
                <a
                  href={`tel:${effectiveProviderData.phone_number}`}
                  className="tw:text-zinc-700 hover:tw:text-zinc-900 tw:!no-underline"
                >
                  {effectiveProviderData.phone_number}
                </a>
              </div>
            )}
          </div>

          {/* User card (unauthenticated) */}
          {assignmentRequest.email && !user && (
            <div className="tw:flex-1 tw:border-l tw:border-zinc-200 tw:pl-4 tw:lg:border-l-0 tw:lg:pl-0 tw:lg:border-t tw:lg:pt-4">
              <div className="tw:flex tw:flex-col tw:items-center tw:text-center">
                <div className="tw:h-12 tw:w-12 tw:md:h-16 tw:md:w-16 tw:rounded-full tw:bg-brand-green-0/10 tw:flex tw:items-center tw:justify-center tw:text-brand-green-0 tw:text-lg tw:md:text-2xl tw:!mb-3">
                  <i className="fas fa-xl fa-id-badge" />
                </div>
                <div>
                  <p className="tw:text-xs tw:md:text-sm tw:font-semibold tw:text-zinc-900 tw:!mb-1 tw:break-all tw:md:break-normal">
                    {assignmentRequest.email}
                  </p>
                  {!(potentialSchedule && potentialScheduleShiftSettings) && (
                    <div className="tw:flex tw:flex-col tw:items-center tw:text-center tw:!mt-1">
                      <button
                        type="button"
                        className="tw:!text-xs tw:text-brand-green-1 tw:underline tw:cursor-pointer tw:!mb-0 tw:bg-transparent tw:border-none tw:p-0"
                        onClick={() => {
                          setInitialAssignmentRequest({ ...assignmentRequest });
                          setInitialUserResponse(userResponse);
                          setAssignmentRequest({});
                          setUserResponse(null);
                          setIsEmailChanging(true);
                          setIsFacilityModalOpen(true);
                        }}
                      >
                        Change
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Facility card */}
          {hasFacility && (
            <div className="tw:flex-1 tw:border-l tw:border-zinc-200 tw:pl-4 tw:lg:border-l-0 tw:lg:pl-0 tw:lg:border-t tw:lg:pt-4">
              <div className="tw:flex tw:flex-col tw:items-center tw:text-center">
                <div className="tw:h-12 tw:w-12 tw:md:h-16 tw:md:w-16 tw:rounded-full tw:bg-brand-green-0/10 tw:flex tw:items-center tw:justify-center tw:text-brand-green-0 tw:text-lg tw:md:text-2xl tw:!mb-3">
                  <i className="fas fa-xl fa-hospital" />
                </div>
                <p className="tw:text-xs tw:md:text-sm tw:font-semibold tw:text-zinc-900 tw:!mb-1">
                  {selectedFacility?.name || assignmentRequest.facility_name || 'Facility'}
                </p>
                {(selectedFacility?.city || assignmentRequest.facility_city) &&
                  (selectedFacility?.state || assignmentRequest.facility_state) && (
                    <p className="tw:text-[8pt] tw:md:text-xs tw:text-zinc-500 tw:!mb-0">
                      {selectedFacility?.city || assignmentRequest.facility_city},{' '}
                      {selectedFacility?.state || assignmentRequest.facility_state}
                    </p>
                  )}
                {connectedFacility?.current_status?.code === StatusCodes.AWAIT_PRVLG && (
                  <p>
                    <span className="tw:text-xs tw:bg-stoplight-warning/10 tw:text-stoplight-warning tw:border tw:border-stoplight-warning/40 tw:rounded tw:p-1">
                      Awaiting Privileges
                    </span>
                  </p>
                )}
                {!(potentialSchedule && potentialScheduleShiftSettings) && (
                  <button
                    type="button"
                    className="tw:!text-xs tw:text-brand-green-1 tw:underline tw:cursor-pointer tw:!mt-1 tw:!mb-0 tw:bg-transparent tw:border-none tw:p-0"
                    onClick={() => {
                      setInitialAssignmentRequest({ ...assignmentRequest });
                      setInitialUserResponse(userResponse);
                      setTentativeFacility(
                        selectedFacility ||
                          (assignmentRequest.facility_name
                            ? ({
                                id: 'OTHER',
                                name: assignmentRequest.facility_name,
                                city: assignmentRequest.facility_city || '',
                                state: assignmentRequest.facility_state || undefined,
                              } as any)
                            : null)
                      );
                      setIsFacilityModalOpen(true);
                    }}
                  >
                    Change
                  </button>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Identification & Facility Selection Modal */}
      <Dialog open={isFacilityModalOpen} onClose={handleModalCancel} className="tw:!p-3">
        <form onSubmit={handleEmailSubmit}>
          <DialogTitle className="tw:!text-base tw:!font-semibold tw:!mb-1 tw:p-2 tw:rounded-lg tw:bg-brand-green-0 tw:!text-white">
            {modalState === MODAL_STATE.FACILITY_SELECT
              ? 'Choose Facility'
              : modalState === MODAL_STATE.MANUAL_INPUT
                ? 'Tell us more about yourself and your facility...'
                : 'Please Identify Yourself'}
          </DialogTitle>
          <DialogBody className="tw:!mt-0">
            <Fieldset>
              {modalState === MODAL_STATE.FACILITY_SELECT ? (
                <>
                  <DialogDescription>
                    Where do you wish to schedule {effectiveProviderData?.display_name}?
                  </DialogDescription>
                  <Field>
                    <Select
                      id="facility"
                      options={facilityOptions}
                      value={tentativeFacilityOption}
                      onChange={(option) => {
                        if (option?.value === 'OTHER') {
                          setTentativeFacility({
                            id: 'OTHER' as any,
                            name: '',
                            city: '',
                            state: undefined,
                          });
                        } else {
                          setTentativeFacility(option?.facility || null);
                        }
                      }}
                      placeholder="Start typing to search"
                      className="tw:w-full"
                      styles={reactSelectReset}
                      isClearable={false}
                    />
                  </Field>
                  {isOtherFacilitySelected && (
                    <>
                      <FieldGroup className="tw:mt-4">
                        <Field>
                          <Input
                            id="other-facility-name"
                            type="text"
                            value={tentativeFacility?.name || ''}
                            onChange={(e) =>
                              setTentativeFacility((prev) => ({
                                id: 'OTHER' as any,
                                name: e.target.value,
                                city: prev?.city || '',
                                state: prev?.state,
                              }))
                            }
                            required
                            className="tw:w-full"
                            placeholder="Facility Name"
                          />
                        </Field>
                      </FieldGroup>
                      <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:my-4">
                        <Field>
                          <Input
                            id="other-facility-city"
                            type="text"
                            value={tentativeFacility?.city || ''}
                            onChange={(e) =>
                              setTentativeFacility((prev) => ({
                                id: 'OTHER' as any,
                                name: prev?.name || '',
                                city: e.target.value,
                                state: prev?.state,
                              }))
                            }
                            required
                            className="tw:w-full"
                            placeholder="Facility City"
                          />
                        </Field>
                        <Field>
                          <Input
                            id="other-facility-state"
                            type="text"
                            value={tentativeFacility?.state || ''}
                            onChange={(e) =>
                              setTentativeFacility((prev) => ({
                                id: 'OTHER' as any,
                                name: prev?.name || '',
                                city: prev?.city || '',
                                state: e.target.value as any,
                              }))
                            }
                            required
                            className="tw:w-full"
                            placeholder="Facility State"
                          />
                        </Field>
                      </div>
                    </>
                  )}
                </>
              ) : modalState === MODAL_STATE.MANUAL_INPUT ? (
                <>
                  <DialogDescription className="tw:!mt-0 tw:!pt-0">
                    We did not find a record of your email address. Please enter your name and some
                    information about the facility at which you would like to schedule{' '}
                    {effectiveProviderData?.display_name}.
                  </DialogDescription>
                  <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:my-4">
                    <Field>
                      <Input
                        id="first-name"
                        type="text"
                        value={assignmentRequest.first_name || ''}
                        onChange={(e) =>
                          setAssignmentRequest((prev) => ({ ...prev, first_name: e.target.value }))
                        }
                        className="tw:w-full"
                        placeholder="Your First Name"
                      />
                    </Field>
                    <Field>
                      <Input
                        id="last-name"
                        type="text"
                        value={assignmentRequest.last_name || ''}
                        onChange={(e) =>
                          setAssignmentRequest((prev) => ({ ...prev, last_name: e.target.value }))
                        }
                        className="tw:w-full"
                        placeholder="Your Last Name"
                      />
                    </Field>
                  </div>
                  <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:my-4">
                    <Field>
                      <Input
                        id="title"
                        type="text"
                        value={assignmentRequest.title || ''}
                        onChange={(e) =>
                          setAssignmentRequest((prev) => ({ ...prev, title: e.target.value }))
                        }
                        className="tw:w-full"
                        placeholder="Your Title"
                      />
                    </Field>
                  </div>
                  <FieldGroup>
                    <Field>
                      <Input
                        id="facility-name"
                        type="text"
                        value={tentativeFacility?.name || ''}
                        onChange={(e) =>
                          setTentativeFacility((prev) => ({
                            name: e.target.value,
                            city: prev?.city || '',
                            state: prev?.state,
                          }))
                        }
                        required
                        className="tw:w-full"
                        placeholder="Facility Name"
                      />
                    </Field>
                  </FieldGroup>
                  <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:my-4">
                    <Field>
                      <Input
                        id="facility-city"
                        type="text"
                        value={tentativeFacility?.city || ''}
                        onChange={(e) =>
                          setTentativeFacility((prev) => ({
                            name: prev?.name || '',
                            city: e.target.value,
                            state: prev?.state,
                          }))
                        }
                        required
                        className="tw:w-full"
                        placeholder="Facility City"
                      />
                    </Field>
                    <Field>
                      <Input
                        id="facility-state"
                        type="text"
                        value={tentativeFacility?.state || ''}
                        onChange={(e) =>
                          setTentativeFacility((prev) => ({
                            name: prev?.name || '',
                            city: prev?.city || '',
                            state: e.target.value as any,
                          }))
                        }
                        required
                        className="tw:w-full"
                        placeholder="Facility State"
                      />
                    </Field>
                  </div>
                </>
              ) : (
                <>
                  <Description>
                    Enter your work email address associated with the organization making the
                    assignment request.
                  </Description>
                  <Field>
                    <Input
                      id="email"
                      type="email"
                      value={assignmentRequest.email || ''}
                      onChange={(e) =>
                        setAssignmentRequest((prev) => ({ ...prev, email: e.target.value }))
                      }
                      placeholder="Email Address"
                      className="tw:w-full"
                    />
                  </Field>
                </>
              )}
            </Fieldset>
          </DialogBody>
          <DialogActions>
            <Button type="button" plain onClick={handleModalCancel}>
              Cancel
            </Button>
            {modalState === MODAL_STATE.FACILITY_SELECT ? (
              <Button
                type="button"
                color="brandgreen0"
                disabled={
                  !tentativeFacility ||
                  (isOtherFacilitySelected &&
                    (!tentativeFacility?.name?.trim() ||
                      !tentativeFacility?.city?.trim() ||
                      !tentativeFacility?.state))
                }
                onClick={handleFacilityConfirm}
              >
                Continue
              </Button>
            ) : modalState === MODAL_STATE.MANUAL_INPUT ? (
              <Button
                type="button"
                color="brandgreen0"
                disabled={
                  !tentativeFacility?.name?.trim() ||
                  !tentativeFacility?.city?.trim() ||
                  !tentativeFacility?.state
                }
                onClick={() => {
                  if (tentativeFacility) {
                    setAssignmentRequest((prev) => ({
                      ...prev,
                      facility: undefined,
                      facility_name: tentativeFacility.name?.trim() || null,
                      facility_city: tentativeFacility.city?.trim() || null,
                      facility_state: tentativeFacility.state
                        ? (tentativeFacility.state as unknown as string)
                        : null,
                    }));
                    setSelectedFacility(null);
                    setIsFacilityModalOpen(false);
                    setTentativeFacility(null);
                    setInitialAssignmentRequest(null);
                    setInitialUserResponse(null);
                  }
                }}
              >
                Continue
              </Button>
            ) : (
              <Button
                type="submit"
                color="brandgreen0"
                disabled={isSubmitting || !assignmentRequest.email?.trim()}
                onClick={() => {
                  setInitialAssignmentRequest(assignmentRequest);
                  setIsEmailChanging(false);
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Continue'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default AssignmentCreateWrapper;
